/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { primitiveColorTokensV2 } from '@uber/base-specs/src/generated/base-color-tokens-v2';
import { HIERARCHY, KIND } from './constants';
import type { Hierarchy, Kind } from './types';
import type { ThemeMode } from '../helpers/types';

/**
 * `banner-content-onsubtle` — identical across every `kind`, unlike
 * `banner-content-onbold` below.
 */
const V2_CONTENT_ONSUBTLE = {
  light: primitiveColorTokensV2.neutral18,
  dark: primitiveColorTokensV2.neutral96
};
type V2ContentOnBoldSwatches = {
  light: string;
  dark: string;
};

/**
 * `banner-content-onbold` swatches, keyed by `kind`. Sourced from the Figma
 * spec; "white" is `neutral100`.
 */
const V2_CONTENT_ONBOLD: Record<Kind, V2ContentOnBoldSwatches> = {
  [KIND.info]: {
    light: primitiveColorTokensV2.neutral100,
    dark: primitiveColorTokensV2.neutral96
  },
  [KIND.negative]: {
    light: primitiveColorTokensV2.neutral100,
    dark: primitiveColorTokensV2.red99
  },
  [KIND.positive]: {
    light: primitiveColorTokensV2.neutral100,
    dark: primitiveColorTokensV2.green15
  },
  [KIND.warning]: {
    light: primitiveColorTokensV2.neutral18,
    dark: primitiveColorTokensV2.orange15
  }
};

/**
 * Resolves Banner's content (title/message text, and the color handed to its
 * action button) color from the Base 2.0 color spec. Only call this when
 * Base 2.0 colors are active (`useBase2Features().colors`); v1 themes keep
 * using the existing `contentPrimary` / `contentOnColor` theme color tokens
 * instead.
 */
export function getV2ContentColor(kind: Kind, hierarchy: Hierarchy, themeMode: ThemeMode | undefined): string {
  const isDark = themeMode === 'dark';
  if (hierarchy === HIERARCHY.high) {
    const swatches = V2_CONTENT_ONBOLD[kind] ?? V2_CONTENT_ONBOLD[KIND.info];
    return isDark ? swatches.dark : swatches.light;
  }
  return isDark ? V2_CONTENT_ONSUBTLE.dark : V2_CONTENT_ONSUBTLE.light;
}